import * as React from 'react'
import { Button } from '@/components/ui/button'
import { SheetFooter } from '@/components/ui/sheet'

import { TransferDestinationEditor } from './TransferDestinationEditor'
import { TransferDestinationList } from './TransferDestinationList'
import {
  emptyDraftDestination,
  toDraftDestination,
  type DraftDestination,
} from '@/features/tools/components/transfer-call/transferDraft'
import type { TransferDestination } from '@/features/tools/types'
import type {
  NormalizedTransferDestination,
  TransferCallSettings,
} from '@/features/tools/types'
import {
  normalizedTransferDestination,
  normalizedTransferSettingsError,
} from './transferSettings'
import { useInitFromServer } from '@/hooks/useInitFromServer'

type View = { mode: 'list' } | { mode: 'add' } | { mode: 'edit'; id: string }

function settingsToDrafts(settings: TransferCallSettings): DraftDestination[] {
  return settings.destinations.map((destination, index) => ({
    ...toDraftDestination(destination as unknown as TransferDestination),
    id: `destination-${index}`,
    raw: destination as unknown as TransferDestination,
  }))
}

export function OrganizationTransferForm({
  settings,
  onSubmit,
  onCancel,
}: {
  settings: TransferCallSettings
  onCancel?: () => void
  onSubmit: (settings: TransferCallSettings) => Promise<void>
}) {
  const settingsError = normalizedTransferSettingsError(settings)
  const serverDrafts = React.useMemo(
    () => settingsToDrafts(settings),
    [settings]
  )
  const [destinations, setDestinations] = React.useState(serverDrafts)
  const [view, setView] = React.useState<View>({ mode: 'list' })
  const [submitting, setSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  useInitFromServer(serverDrafts, setDestinations)

  if (settingsError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {settingsError}
      </p>
    )
  }

  const persist = async (
    next: DraftDestination[],
    changedDestinationIds: ReadonlySet<string>
  ): Promise<boolean> => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const normalized = next.map((destination) =>
        changedDestinationIds.has(destination.id)
          ? normalizedTransferDestination(destination)
          : (destination.raw as unknown as NormalizedTransferDestination)
      )
      const nextSettings: TransferCallSettings = {
        version: 1,
        destinations: normalized,
      }
      const validationError = normalizedTransferSettingsError(nextSettings)
      if (validationError) {
        setSubmitError(validationError)
        return false
      }
      await onSubmit(nextSettings)
      setDestinations(next)
      return true
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to save Transfer settings'
      )
      return false
    } finally {
      setSubmitting(false)
    }
  }

  if (view.mode === 'add') {
    return (
      <>
        <TransferDestinationEditor
          mode="add"
          initial={emptyDraftDestination()}
          isSaving={submitting}
          isArchived={false}
          settingsOwner="organization"
          onBack={() => setView({ mode: 'list' })}
          onSave={async (draft) => {
            if (await persist([...destinations, draft], new Set([draft.id]))) {
              setView({ mode: 'list' })
            }
          }}
        />
        <SubmitError error={submitError} />
      </>
    )
  }

  if (view.mode === 'edit') {
    const editing = destinations.find(
      (destination) => destination.id === view.id
    )
    if (editing) {
      return (
        <>
          <TransferDestinationEditor
            mode="edit"
            initial={editing}
            isSaving={submitting}
            isArchived={false}
            settingsOwner="organization"
            onBack={() => setView({ mode: 'list' })}
            onSave={async (draft) => {
              const next = destinations.map((destination) =>
                destination.id === view.id ? draft : destination
              )
              if (await persist(next, new Set([view.id]))) {
                setView({ mode: 'list' })
              }
            }}
            onDelete={async () => {
              const next = destinations.filter(
                (destination) => destination.id !== view.id
              )
              if (await persist(next, new Set())) setView({ mode: 'list' })
            }}
          />
          <SubmitError error={submitError} />
        </>
      )
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <TransferDestinationList
          destinations={destinations}
          isExpanded
          isArchived={submitting}
          onAdd={() => setView({ mode: 'add' })}
          onEdit={(id) => setView({ mode: 'edit', id })}
        />
        <SubmitError error={submitError} />
      </div>
      {onCancel && (
        <SheetFooter className="shrink-0 flex-row justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Close
          </Button>
        </SheetFooter>
      )}
    </div>
  )
}

function SubmitError({ error }: { error: string | null }) {
  return error ? (
    <p role="alert" className="text-sm text-destructive">
      {error}
    </p>
  ) : null
}
